import React, { useEffect, useMemo, useState } from 'react';
import { ChildProfile } from '../models/childProfile';
import ChildAvatar from './ChildAvatar';

interface Props {
  childProfiles: ChildProfile[];
  selectedChildId: string | null;
  onSelectChild: (childId: string | null) => void;
  /** Sentence under the heading, naming what the choice unlocks. */
  subtitle?: string;
  /** Set by sections that are meaningful without a child (schedule,
   *  attendance) — adds an explicit "All children" tile so the picker can
   *  express the unfiltered scope instead of forcing a single child. */
  allowAll?: boolean;
  /** Collapses back to the compact sidebar without changing the selection.
   *  Present when there's a selection to fall back to, or when "All children"
   *  is itself a valid destination. */
  onCollapse?: () => void;
}

// Above this many children the grid gets a search box; below it, scanning the
// tiles is quicker than typing.
const SEARCH_FROM = 9;

const TILE_AVATAR = 88;

/**
 * Full-area child picker shown by the sections that only make sense for one
 * child (posts, insights, reports, child documents) while no child is chosen.
 * Once a child is picked the compact 110px sidebar takes over; its toggle
 * brings this panel back.
 */
const ChildSelectorPanel: React.FC<Props> = ({
  childProfiles,
  selectedChildId,
  onSelectChild,
  subtitle,
  allowAll = false,
  onCollapse,
}) => {
  const [query, setQuery] = useState('');

  // Only active children can be worked with. Shared app state may still hold
  // the Admin screen's list — which deliberately includes inactive children so
  // they can be reactivated — for the moment between arriving here and this
  // section's own fetch landing, so filter at the point of display rather than
  // trusting whatever the last screen loaded.
  const list = useMemo(
    () => (childProfiles || []).filter((c) => c.is_active),
    [childProfiles]
  );
  const showSearch = list.length >= SEARCH_FROM;

  // Looked up in the unfiltered set: a child deactivated elsewhere while open
  // should still name the tile you'd go back to.
  const selectedChild = useMemo(
    () => (selectedChildId ? (childProfiles || []).find((c) => String(c.id) === selectedChildId) : null),
    [childProfiles, selectedChildId]
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) =>
      `${c.first_name} ${c.last_name} ${c.alias || ''} ${c.initials || ''}`
        .toLowerCase()
        .includes(q)
    );
  }, [list, query]);

  // Escape backs out of the panel — only meaningful when there's a selection
  // to fall back to.
  useEffect(() => {
    if (!onCollapse) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCollapse();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCollapse]);

  return (
    <div className="child-picker">
      <div className="child-picker__head">
        {/* Only present when there's a selection to go back to. */}
        {onCollapse && (
          <button type="button" className="child-picker__back" onClick={onCollapse}>
            <i className="fas fa-arrow-left" aria-hidden="true" />
            <span>
              {selectedChild
                ? `Back to ${selectedChild.first_name} ${selectedChild.last_name}`
                : allowAll ? 'Back to all children' : 'Back'}
            </span>
          </button>
        )}

        <div className="child-picker__head-main">
          <div className="child-picker__headings">
            {/* "Select a child" would contradict the All children tile. */}
            <h2 className="child-picker__title">
              {allowAll ? 'Choose who to show' : 'Select a child'}
            </h2>
            {subtitle && <div className="child-picker__subtitle">{subtitle}</div>}
          </div>

          {showSearch && (
            <div className="child-picker__search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search children"
                aria-label="Search children"
              />
            </div>
          )}
        </div>
      </div>

      <div className="child-picker__grid">
        {/* Scope tile, not a person — hidden while searching, since a query is
            always a search for a child. Uses the same asterisk the compact
            sidebar's clear-filter avatar does, so the two surfaces agree. */}
        {allowAll && !query.trim() && (
          <button
            type="button"
            className={`child-card child-picker__card child-picker__card--all ${!selectedChildId ? 'is-selected' : ''}`}
            onClick={() => onSelectChild(null)}
            aria-pressed={!selectedChildId}
            // Help rings the tiles, not the panel: the anchor sits on every
            // card and the spotlight takes their union, so the ring hugs the
            // choices rather than the whole screen.
            data-help="child-picker"
          >
            <span className="child-picker__all-glyph" aria-hidden="true">
              <i className="fas fa-asterisk" />
            </span>
            <div className="child-card__body">
              <div className="child-card__name">All children</div>
              <div className="child-picker__all-sub">
                {list.length} {list.length === 1 ? 'child' : 'children'}
              </div>
            </div>
            {!selectedChildId && (
              <div className="child-picker__card-check" aria-hidden="true">
                <i className="fas fa-check" />
              </div>
            )}
          </button>
        )}

        {visible.map((c) => {
          const isSelected = String(c.id) === selectedChildId;
          return (
            <button
              key={c.id}
              type="button"
              className={`child-card child-picker__card ${isSelected ? 'is-selected' : ''}`}
              onClick={() => onSelectChild(String(c.id))}
              aria-pressed={isSelected}
              title={`${c.first_name} ${c.last_name}`}
              data-help="child-picker"
            >
              <ChildAvatar
                child={c}
                size={TILE_AVATAR}
                // Full-size (400px) image, not the 100px thumbnail — these
                // tiles render the face at 88px, which is already past the
                // thumbnail's resolution on a 2× screen.
                imageUrl={c.profile_image_url || c.profile_image_thumbnail_url || null}
                showBadge={true}
              />
              <div className="child-card__body">
                <div className="child-card__name">{c.first_name} {c.last_name}</div>
                {c.alias && <div className="child-card__alias">"{c.alias}"</div>}
              </div>
              {isSelected && (
                <div className="child-picker__card-check" aria-hidden="true">
                  <i className="fas fa-check" />
                </div>
              )}
              <div className="child-card__bar" style={{ background: c.colour_code }} aria-hidden="true" />
            </button>
          );
        })}

        {visible.length === 0 && (
          <div className="child-picker__empty">
            {list.length === 0
              ? 'No children are available to you in this organisation.'
              : <>No children match <strong>"{query}"</strong>.</>}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChildSelectorPanel;
